import { getApp, getApps, initializeApp } from 'firebase/app';
import type { Auth, User } from 'firebase/auth';
import type { Firestore, FirestoreError } from 'firebase/firestore';
import type { FirebaseStorage } from 'firebase/storage';

import { FIREBASE_CONFIG, FIRESTORE_ENABLED, STORAGE_ENABLED } from '@/config/env';
import { sanitizeUpload } from './sanitize';

export type { FirestoreError, User as AuthUser };

/** One stored document: its id and its fields. */
export interface StoredDocument {
  id: string;
  raw: Record<string, unknown>;
}

export type FilterOperator = '==' | 'array-contains';

export type Filter = readonly [field: string, operator: FilterOperator, value: unknown];

/** One collection query: the path, the filters, the order and the row limit. */
export interface CollectionQuery {
  path: string;
  limit: number;
  orderByField?: string;
  direction: 'asc' | 'desc';
  filters: readonly Filter[];
}

type RowsListener = (rows: StoredDocument[]) => void;

type ErrorListener = (error: FirestoreError) => void;

let firestore: Firestore | null = null;
let storage: FirebaseStorage | null = null;

/** Returns the Firebase app, and starts it on first use. */
export function firebaseApp() {
  return getApps().length ? getApp() : initializeApp(FIREBASE_CONFIG);
}

/** Returns the Firestore client, and loads the SDK on first use. */
export async function db(): Promise<Firestore> {
  if (!FIRESTORE_ENABLED) {
    throw new Error('Firestore is not configured — guard callers with FIRESTORE_ENABLED.');
  }
  if (!firestore) {
    const { getFirestore } = await import('firebase/firestore');
    firestore = getFirestore(firebaseApp());
  }
  return firestore;
}

/** Returns the Storage client, and loads the SDK on first use. */
async function bucket(): Promise<FirebaseStorage> {
  if (!STORAGE_ENABLED) {
    throw new Error('Cloud Storage is not configured — guard callers with STORAGE_ENABLED.');
  }
  if (!storage) {
    const { getStorage } = await import('firebase/storage');
    storage = getStorage(firebaseApp());
  }
  return storage;
}

/** Returns the Auth client, and loads the SDK on first use. */
async function auth(): Promise<Auth> {
  const { getAuth } = await import('firebase/auth');
  return getAuth(firebaseApp());
}

/** Opens the Google popup and returns the signed-in user. */
export async function signInWithGoogle(): Promise<User> {
  const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
  const credential = await signInWithPopup(await auth(), new GoogleAuthProvider());
  return credential.user;
}

/** Returns the signed-in user, or null when nobody is signed in. */
export async function currentUser(): Promise<User | null> {
  return (await auth()).currentUser;
}

/** Signs the current user out of Firebase. */
export async function signOutOfFirebase(): Promise<void> {
  const { signOut } = await import('firebase/auth');
  await signOut(await auth());
}

/** Sends the user to `onUser` on each ID token change, or null when nobody is signed in.
 *  @return The function that stops the listener. */
export async function watchIdToken(onUser: (user: User | null) => void): Promise<() => void> {
  const { onIdTokenChanged } = await import('firebase/auth');
  return onIdTokenChanged(await auth(), onUser);
}

/** Returns the fields of one document, or null when it is absent. */
export async function readDocument(
  path: string,
  documentId: string,
): Promise<Record<string, unknown> | null> {
  const { doc, getDoc } = await import('firebase/firestore');
  const snapshot = await getDoc(doc(await db(), path, documentId));
  return snapshot.exists() ? snapshot.data() : null;
}

/** Returns the number of documents in one collection, from one aggregate read. */
export async function countDocuments(path: string): Promise<number> {
  const { collection, getCountFromServer } = await import('firebase/firestore');
  const snapshot = await getCountFromServer(collection(await db(), path));
  return snapshot.data().count;
}

/** Sends the rows of one query to `onRows` on each change.
 *  @return The function that stops the listener. */
export async function watchCollection(spec: CollectionQuery, onRows: RowsListener, onError: ErrorListener): Promise<() => void> {
  const { collection, limit, onSnapshot, orderBy, query, where } = await import('firebase/firestore');
  const constraints = [
    ...spec.filters.map(([field, operator, value]) => where(field, operator, value)),
    ...(spec.orderByField ? [orderBy(spec.orderByField, spec.direction)] : []),
    limit(spec.limit),
  ];
  return onSnapshot(
    query(collection(await db(), spec.path), ...constraints),
    (snapshot) => onRows(snapshot.docs.map((entry) => ({ id: entry.id, raw: entry.data() }))),
    onError,
  );
}

/** Sends one document to `onRows` on each change: one row, or none when it is absent.
 *  @return The function that stops the listener. */
export async function watchDocument(path: string, onRows: RowsListener, onError: ErrorListener): Promise<() => void> {
  const { doc, onSnapshot } = await import('firebase/firestore');
  return onSnapshot(
    doc(await db(), path),
    (snapshot) => onRows(snapshot.exists() ? [{ id: snapshot.id, raw: snapshot.data() }] : []),
    onError,
  );
}

/** Returns the download URL of one object in the bucket. */
export async function objectUrl(objectName: string): Promise<string> {
  const { getDownloadURL, ref } = await import('firebase/storage');
  return getDownloadURL(ref(await bucket(), objectName));
}

/** Returns the full name of every object directly in one folder. */
export async function listFolder(folder: string): Promise<string[]> {
  const { listAll, ref } = await import('firebase/storage');
  const { items } = await listAll(ref(await bucket(), folder));
  return items.map((item) => item.fullPath);
}

/** Returns the Firebase download URL of an object, the address the storage rules
 *  apply to. */
export function publicObjectUrl(objectName: string): string {
  const path = encodeURIComponent(objectName);
  return `https://firebasestorage.googleapis.com/v0/b/${FIREBASE_CONFIG.storageBucket}/o/${path}?alt=media`;
}

/** Stores one file in the bucket, sanitized first. */
export async function uploadObject(objectName: string, file: File): Promise<void> {
  const { ref, uploadBytes } = await import('firebase/storage');
  await uploadBytes(ref(await bucket(), objectName), await sanitizeUpload(file));
}
